import React, { useCallback, useEffect, useState } from 'react';
import { Package, Percent, Receipt, RefreshCw, Plus, Copy, Archive, Eye, EyeOff, Play, Pause, History, Trash2 } from 'lucide-react';
import api from '@/lib/axios';

const BADGES = {
  preco_por_definir: { label: 'Preço por definir', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  inativo: { label: 'Inativo', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  agendado: { label: 'Agendado', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  indisponivel: { label: 'Indisponível', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  arquivado: { label: 'Arquivado', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
  disponivel: { label: 'Disponível', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

const Badge = ({ state }) => {
  const b = BADGES[state] || BADGES.inativo;
  return <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${b.cls}`}>{b.label}</span>;
};

const Field = ({ label, children }) => (
  <label className="block text-xs font-semibold text-slate-600">
    {label}
    <div className="mt-1">{children}</div>
  </label>
);

const input = 'w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm';

export default function MarketplaceCommercePanel({ initialTab = 'produtos', showTabs = true }) {
  const [tab, setTab] = useState(initialTab);
  const [products, setProducts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [promos, setPromos] = useState([]);
  const [orders, setOrders] = useState({ items: [], total: 0, counts_by_status: {} });
  const [orderFilters, setOrderFilters] = useState({ status: '', email: '', order_number: '', environment: '' });
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [versions, setVersions] = useState(null);
  const [newCampaign, setNewCampaign] = useState(null);
  const [newPromo, setNewPromo] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null);
  const [rowBusy, setRowBusy] = useState('');
  const [ordersFiltered, setOrdersFiltered] = useState(false);

  const notify = (m, isErr = false) => {
    if (isErr) { setErr(m); setMsg(''); } else { setMsg(m); setErr(''); }
    setTimeout(() => { setMsg(''); setErr(''); }, 6000);
  };

  const readError = (e) => {
    const d = e?.response?.data?.detail;
    if (Array.isArray(d?.errors)) return d.errors.join(' · ');
    if (typeof d === 'string') return d;
    return 'Não foi possível concluir a operação';
  };

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [p, c, pc, o] = await Promise.all([
        api.get('/admin/marketplace/products?include_archived=true'),
        api.get('/admin/marketplace/campaigns?include_archived=true'),
        api.get('/admin/marketplace/promo-codes?include_archived=true'),
        api.get('/admin/marketplace/orders?limit=50'),
      ]);
      setProducts(p.data.items || []);
      setCampaigns(c.data.items || []);
      setPromos(pc.data.items || []);
      setOrders(o.data || { items: [] });
    } catch (e) {
      notify(readError(e), true);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadOrders = async () => {
    const active = Object.entries(orderFilters).filter(([, v]) => v);
    const qs = new URLSearchParams(active).toString();
    try {
      const r = await api.get(`/admin/marketplace/orders?limit=50${qs ? `&${qs}` : ''}`);
      setOrders(r.data);
      setOrdersFiltered(active.length > 0);
    } catch (e) { notify(readError(e), true); }
  };

  const reloadList = async (kind) => {
    const url = {
      products: '/admin/marketplace/products?include_archived=true',
      campaigns: '/admin/marketplace/campaigns?include_archived=true',
      promos: '/admin/marketplace/promo-codes?include_archived=true',
    }[kind];
    const setter = { products: setProducts, campaigns: setCampaigns, promos: setPromos }[kind];
    const r = await api.get(url);
    setter(r.data.items || []);
  };

  const showOrder = async (o) => {
    setRowBusy(o.id);
    try {
      const r = await api.get(`/admin/marketplace/orders/${o.id}`);
      setOrderDetail(r.data);
    } catch (e) { notify(readError(e), true); } finally { setRowBusy(''); }
  };

  const seed = async () => {
    try {
      const r = await api.post('/admin/marketplace/products/seed-models');
      notify(`${r.data.created.length} modelos criados (${r.data.skipped.length} já existiam) — todos inativos e sem preço`);
      load();
    } catch (e) { notify(readError(e), true); }
  };

  const productAction = async (id, action) => {
    setRowBusy(id);
    try {
      await api.post(`/admin/marketplace/products/${id}/state`, { action });
      await reloadList('products');
      notify('Produto atualizado');
    } catch (e) { notify(readError(e), true); } finally { setRowBusy(''); }
  };

  const saveProduct = async () => {
    const body = { ...editing };
    delete body.availability_state; delete body.purchasable; delete body.usage_count;
    ['price', 'tax_rate', 'duration_days', 'extension_days', 'display_order', 'max_uses_total', 'max_uses_per_email', 'max_uses_per_phone'].forEach((k) => {
      body[k] = body[k] === '' || body[k] === null || body[k] === undefined ? null : Number(body[k]);
    });
    try {
      if (editing.id) await api.put(`/admin/marketplace/products/${editing.id}`, body);
      else await api.post('/admin/marketplace/products', body);
      notify('Produto guardado');
      setEditing(null);
      load();
    } catch (e) { notify(readError(e), true); }
  };

  const deleteProduct = async (p) => {
    try {
      await api.delete(`/admin/marketplace/products/${p.id}`);
      notify('Produto apagado');
      load();
    } catch (e) { notify(readError(e), true); }
  };

  const showVersions = async (p) => {
    const r = await api.get(`/admin/marketplace/products/${p.id}/versions`);
    setVersions({ product: p, items: r.data.items || [] });
  };

  const campaignAction = async (id, action) => {
    setRowBusy(id);
    try {
      await api.post(`/admin/marketplace/campaigns/${id}/state`, { action });
      await reloadList('campaigns');
      notify('Campanha atualizada');
    } catch (e) { notify(readError(e), true); } finally { setRowBusy(''); }
  };

  const promoAction = async (id, action) => {
    setRowBusy(id);
    try {
      await api.post(`/admin/marketplace/promo-codes/${id}/state`, { action });
      await reloadList('promos');
      notify('Código atualizado');
    } catch (e) { notify(readError(e), true); } finally { setRowBusy(''); }
  };

  const launchTemplate = async () => {
    const r = await api.get('/admin/marketplace/campaigns/launch-template');
    const t = { ...r.data.template };
    if (!t.starts_at) t.starts_at = new Date().toISOString().replace('.000Z', '+00:00').replace(/\.\d{3}Z$/, '+00:00');
    if (!t.ends_at) t.ends_at = new Date(Date.now() + 30 * 864e5).toISOString().replace(/\.\d{3}Z$/, '+00:00');
    setNewCampaign(t);
    setTab('campanhas');
  };

  const tabs = [
    { key: 'produtos', label: 'Produtos e preços', icon: Package },
    { key: 'campanhas', label: 'Campanhas e promoções', icon: Percent },
    { key: 'encomendas', label: 'Encomendas e pagamentos', icon: Receipt },
  ];

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5" data-testid="marketplace-commerce-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-slate-900">Gestão comercial</h2>
          <p className="text-xs text-slate-500">Produtos, preços, campanhas e encomendas · comércio ainda desativado</p>
        </div>
        <button onClick={load} disabled={busy} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-1.5 text-sm" data-testid="commerce-refresh">
          <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} /> Atualizar
        </button>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`commerce-tab-${key}`}
            className={`inline-flex items-center gap-2 rounded-xl border px-3 py-1.5 text-sm ${tab === key ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-slate-300 text-slate-600'}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {msg && <p className="mt-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="commerce-message">{msg}</p>}
      {err && <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="commerce-error">{err}</p>}

      {/* ---------------- PRODUTOS ---------------- */}
      {tab === 'produtos' && (
        <div className="mt-4" data-testid="commerce-products-section">
          <div className="mb-3 flex flex-wrap gap-2">
            <button onClick={() => setEditing({ code: '', name: '', category: 'publicacao', product_type: 'publicacao_aluguer', applicable_to: ['aluguer'], price: '', is_free: false, requires_payment: true })}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white" data-testid="product-new">
              <Plus className="h-4 w-4" /> Novo produto
            </button>
            <button onClick={seed} className="rounded-xl border border-slate-300 px-3 py-1.5 text-sm" data-testid="product-seed">
              Criar modelos iniciais (inativos, sem preço)
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="products-table">
              <thead className="text-[11px] uppercase text-slate-500">
                <tr><th className="py-2">Nome</th><th>Código</th><th>Categoria</th><th>Aplicação</th><th>Duração</th><th>Preço</th><th>Estado</th><th>Público</th><th>Ordem</th><th>Versão</th><th>Ações</th></tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100" data-testid={`product-row-${p.code}`}>
                    <td className="py-2 font-semibold text-slate-800">{p.name}</td>
                    <td className="text-xs text-slate-500">{p.code}</td>
                    <td className="text-xs">{p.category}</td>
                    <td className="text-xs">{(p.applicable_to || []).join(', ')}</td>
                    <td className="text-xs">{p.duration_days ? `${p.duration_days}d` : (p.extension_days ? `+${p.extension_days}d` : '—')}</td>
                    <td className="text-xs">{p.is_free ? 'Gratuito' : (p.price === null || p.price === undefined ? '—' : `${p.price} ${p.currency}`)}</td>
                    <td><Badge state={p.availability_state} /></td>
                    <td className="text-xs">{p.is_public ? 'Sim' : 'Não'}</td>
                    <td className="text-xs">{p.display_order}</td>
                    <td className="text-xs">v{p.version}</td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        <button onClick={() => setEditing({ ...p })} disabled={rowBusy === p.id} className="rounded-lg border px-2 py-1 text-[11px] disabled:opacity-50" data-testid={`product-edit-${p.code}`}>Editar</button>
                        <button title="Duplicar produto" aria-label="Duplicar produto" onClick={() => { setRowBusy(p.id); api.post(`/admin/marketplace/products/${p.id}/duplicate`).then(() => reloadList('products')).then(() => notify('Produto duplicado')).catch((e) => notify(readError(e), true)).finally(() => setRowBusy('')); }} disabled={rowBusy === p.id} className="rounded-lg border px-2 py-1 text-[11px] disabled:opacity-50" data-testid={`product-duplicate-${p.code}`}><Copy className="h-3 w-3" /></button>
                        <button title={p.is_active ? 'Desativar produto' : 'Ativar produto'} aria-label={p.is_active ? 'Desativar produto' : 'Ativar produto'} onClick={() => productAction(p.id, p.is_active ? 'deactivate' : 'activate')} disabled={rowBusy === p.id} className="rounded-lg border px-2 py-1 text-[11px] disabled:opacity-50" data-testid={`product-toggle-active-${p.code}`}>{p.is_active ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}</button>
                        <button title={p.is_public ? 'Esconder do público' : 'Tornar público'} aria-label={p.is_public ? 'Esconder do público' : 'Tornar público'} onClick={() => productAction(p.id, p.is_public ? 'hide' : 'publish')} disabled={rowBusy === p.id} className="rounded-lg border px-2 py-1 text-[11px] disabled:opacity-50" data-testid={`product-toggle-public-${p.code}`}>{p.is_public ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}</button>
                        <button title="Arquivar produto" aria-label="Arquivar produto" onClick={() => productAction(p.id, 'archive')} disabled={rowBusy === p.id} className="rounded-lg border px-2 py-1 text-[11px] disabled:opacity-50" data-testid={`product-archive-${p.code}`}><Archive className="h-3 w-3" /></button>
                        <button title="Histórico de versões" aria-label="Histórico de versões" onClick={() => showVersions(p)} className="rounded-lg border px-2 py-1 text-[11px]" data-testid={`product-versions-${p.code}`}><History className="h-3 w-3" /></button>
                        {p.usage_count === 0 && (
                          <button title="Apagar produto (sem encomendas)" aria-label="Apagar produto" onClick={() => deleteProduct(p)} className="rounded-lg border border-rose-200 px-2 py-1 text-[11px] text-rose-600" data-testid={`product-delete-${p.code}`}><Trash2 className="h-3 w-3" /></button>
                        )}
                        {rowBusy === p.id && <span className="text-[11px] text-slate-500">a atualizar…</span>}
                      </div>
                    </td>
                  </tr>
                ))}
                {!products.length && <tr><td colSpan={11} className="py-4 text-center text-sm text-slate-500">Sem produtos. Crie os modelos iniciais.</td></tr>}
              </tbody>
            </table>
          </div>

          {editing && (
            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/40 p-3" data-testid="product-editor">
              <div className="grid gap-3 md:grid-cols-4">
                <Field label="Código"><input className={input} value={editing.code || ''} onChange={(e) => setEditing({ ...editing, code: e.target.value })} data-testid="product-field-code" /></Field>
                <Field label="Nome"><input className={input} value={editing.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="product-field-name" /></Field>
                <Field label="Categoria">
                  <select className={input} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="product-field-category">
                    {['publicacao', 'prolongamento_renovacao', 'extra', 'pacote_plano', 'publicidade'].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Tipo">
                  <select className={input} value={editing.product_type} onChange={(e) => setEditing({ ...editing, product_type: e.target.value })} data-testid="product-field-type">
                    {['publicacao_aluguer', 'publicacao_venda', 'publicacao_motorista', 'prolongamento', 'renovacao', 'extra_destaque', 'extra_topo', 'extra_urgente', 'extra_destaque_homepage', 'extra_destaque_categoria', 'extra_boost_email', 'pacote_anuncios', 'plano_stand', 'plano_parceiro', 'publicidade'].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Preço (vazio = por definir)"><input className={input} type="number" step="0.01" value={editing.price ?? ''} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="product-field-price" /></Field>
                <Field label="IVA %"><input className={input} type="number" step="0.1" value={editing.tax_rate ?? ''} onChange={(e) => setEditing({ ...editing, tax_rate: e.target.value })} data-testid="product-field-tax" /></Field>
                <Field label="Duração (dias)"><input className={input} type="number" value={editing.duration_days ?? ''} onChange={(e) => setEditing({ ...editing, duration_days: e.target.value })} data-testid="product-field-duration" /></Field>
                <Field label="Dias acrescentados"><input className={input} type="number" value={editing.extension_days ?? ''} onChange={(e) => setEditing({ ...editing, extension_days: e.target.value })} data-testid="product-field-extension" /></Field>
                <Field label="Disponível de"><input className={input} value={editing.available_from ?? ''} onChange={(e) => setEditing({ ...editing, available_from: e.target.value })} placeholder="2026-09-01T00:00:00+00:00" /></Field>
                <Field label="Disponível até"><input className={input} value={editing.available_until ?? ''} onChange={(e) => setEditing({ ...editing, available_until: e.target.value })} /></Field>
                <Field label="Ordem"><input className={input} type="number" value={editing.display_order ?? 100} onChange={(e) => setEditing({ ...editing, display_order: e.target.value })} /></Field>
                <Field label="Stripe Price ID (opcional)"><input className={input} value={editing.stripe_price_id ?? ''} onChange={(e) => setEditing({ ...editing, stripe_price_id: e.target.value })} /></Field>
                <Field label="Limite por email"><input className={input} type="number" value={editing.max_uses_per_email ?? ''} onChange={(e) => setEditing({ ...editing, max_uses_per_email: e.target.value })} /></Field>
                <Field label="Limite por telefone"><input className={input} type="number" value={editing.max_uses_per_phone ?? ''} onChange={(e) => setEditing({ ...editing, max_uses_per_phone: e.target.value })} /></Field>
                <Field label="Texto do botão"><input className={input} value={editing.button_text ?? ''} onChange={(e) => setEditing({ ...editing, button_text: e.target.value })} /></Field>
                <Field label="Badge"><input className={input} value={editing.badge_text ?? ''} onChange={(e) => setEditing({ ...editing, badge_text: e.target.value })} /></Field>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-4 text-xs">
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.is_free} onChange={(e) => setEditing({ ...editing, is_free: e.target.checked, requires_payment: !e.target.checked })} data-testid="product-field-free" /> Gratuito</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.requires_payment} onChange={(e) => setEditing({ ...editing, requires_payment: e.target.checked })} /> Exige pagamento</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} data-testid="product-field-active" /> Ativo</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.is_public} onChange={(e) => setEditing({ ...editing, is_public: e.target.checked })} /> Público</label>
                <input className={`${input} max-w-xs`} placeholder="Motivo da alteração" value={editing.reason ?? ''} onChange={(e) => setEditing({ ...editing, reason: e.target.value })} />
                <button onClick={saveProduct} className="rounded-xl bg-emerald-600 px-4 py-1.5 font-semibold text-white" data-testid="product-save">Guardar</button>
                <button onClick={() => setEditing(null)} className="rounded-xl border px-3 py-1.5" data-testid="product-cancel">Cancelar</button>
              </div>
            </div>
          )}

          {versions && (
            <div className="mt-4 rounded-xl border border-slate-200 p-3" data-testid="product-versions-list">
              <p className="text-sm font-semibold">Histórico de {versions.product.name} ({versions.items.length} versões)</p>
              <ul className="mt-2 space-y-1 text-xs text-slate-600">
                {versions.items.map((v) => (
                  <li key={v.id}>
                    v{v.version} · {v.created_at?.slice(0, 19)} · {v.admin_email || '—'} ·{' '}
                    {Object.keys(v.changed_fields || {}).length
                      ? Object.entries(v.changed_fields).map(([f, d]) => `${f}: ${String(d?.de ?? '—')} → ${String(d?.para ?? '—')}`).join(' · ')
                      : 'snapshot'}
                    {v.reason ? ` · ${v.reason}` : ''}
                  </li>
                ))}
              </ul>
              <button onClick={() => setVersions(null)} className="mt-2 rounded-lg border px-2 py-1 text-xs">Fechar</button>
            </div>
          )}
        </div>
      )}

      {/* ---------------- CAMPANHAS ---------------- */}
      {tab === 'campanhas' && (
        <div className="mt-4 space-y-5" data-testid="commerce-campaigns-section">
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setNewCampaign({ name: '', discount_type: 'percent', discount_value: 10, product_ids: [], priority: 100 })} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white" data-testid="campaign-new"><Plus className="h-4 w-4" /> Nova campanha</button>
            <button onClick={launchTemplate} className="rounded-xl border border-slate-300 px-3 py-1.5 text-sm" data-testid="campaign-launch-template">Usar modelo "Lançamento gratuito"</button>
            <button onClick={() => setNewPromo({ code: '', name: '', discount_type: 'percent', discount_value: 10 })} className="rounded-xl border border-slate-300 px-3 py-1.5 text-sm" data-testid="promo-new">Novo código promocional</button>
          </div>

          {newCampaign && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3" data-testid="campaign-editor">
              <div className="grid gap-3 md:grid-cols-3">
                <Field label="Nome"><input className={input} value={newCampaign.name || ''} onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })} data-testid="campaign-field-name" /></Field>
                <Field label="Início"><input className={input} value={newCampaign.starts_at || ''} onChange={(e) => setNewCampaign({ ...newCampaign, starts_at: e.target.value })} placeholder="2026-09-01T00:00:00+00:00" data-testid="campaign-field-start" /></Field>
                <Field label="Fim"><input className={input} value={newCampaign.ends_at || ''} onChange={(e) => setNewCampaign({ ...newCampaign, ends_at: e.target.value })} data-testid="campaign-field-end" /></Field>
                <Field label="Tipo de desconto">
                  <select className={input} value={newCampaign.discount_type} onChange={(e) => setNewCampaign({ ...newCampaign, discount_type: e.target.value })}>
                    {['percent', 'amount', 'free'].map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </Field>
                <Field label="Valor do desconto"><input className={input} type="number" value={newCampaign.discount_value ?? ''} onChange={(e) => setNewCampaign({ ...newCampaign, discount_value: Number(e.target.value) })} /></Field>
                <Field label="Prioridade"><input className={input} type="number" value={newCampaign.priority ?? 100} onChange={(e) => setNewCampaign({ ...newCampaign, priority: Number(e.target.value) })} /></Field>
                <Field label="Limite total"><input className={input} type="number" value={newCampaign.max_uses_total ?? ''} onChange={(e) => setNewCampaign({ ...newCampaign, max_uses_total: e.target.value ? Number(e.target.value) : null })} /></Field>
                <Field label="Limite por email"><input className={input} type="number" value={newCampaign.max_uses_per_email ?? ''} onChange={(e) => setNewCampaign({ ...newCampaign, max_uses_per_email: e.target.value ? Number(e.target.value) : null })} /></Field>
                <Field label="Limite por telefone"><input className={input} type="number" value={newCampaign.max_uses_per_phone ?? ''} onChange={(e) => setNewCampaign({ ...newCampaign, max_uses_per_phone: e.target.value ? Number(e.target.value) : null })} /></Field>
                <Field label="Mensagem pública"><input className={input} value={newCampaign.public_message || ''} onChange={(e) => setNewCampaign({ ...newCampaign, public_message: e.target.value })} /></Field>
                <Field label="Produtos abrangidos">
                  <select multiple className={`${input} h-24`} value={newCampaign.product_ids || []}
                    onChange={(e) => setNewCampaign({ ...newCampaign, product_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })} data-testid="campaign-field-products">
                    {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </Field>
                <label className="flex items-end gap-2 text-xs"><input type="checkbox" checked={!!newCampaign.makes_product_free} onChange={(e) => setNewCampaign({ ...newCampaign, makes_product_free: e.target.checked })} /> Torna o produto gratuito</label>
              </div>
              <div className="mt-3 flex gap-2">
                <button onClick={() => api.post('/admin/marketplace/campaigns', newCampaign).then(() => { notify('Campanha criada em rascunho'); setNewCampaign(null); load(); }).catch((e) => notify(readError(e), true))} className="rounded-xl bg-emerald-600 px-4 py-1.5 text-sm font-semibold text-white" data-testid="campaign-save">Guardar rascunho</button>
                <button onClick={() => setNewCampaign(null)} className="rounded-xl border px-3 py-1.5 text-sm">Cancelar</button>
              </div>
            </div>
          )}

          {newPromo && (
            <div className="rounded-xl border border-slate-200 p-3" data-testid="promo-editor">
              <div className="grid gap-3 md:grid-cols-4">
                <Field label="Código"><input className={input} value={newPromo.code || ''} onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value })} data-testid="promo-field-code" /></Field>
                <Field label="Nome"><input className={input} value={newPromo.name || ''} onChange={(e) => setNewPromo({ ...newPromo, name: e.target.value })} /></Field>
                <Field label="Tipo"><select className={input} value={newPromo.discount_type} onChange={(e) => setNewPromo({ ...newPromo, discount_type: e.target.value })}>{['percent', 'amount', 'free'].map((d) => <option key={d} value={d}>{d}</option>)}</select></Field>
                <Field label="Valor"><input className={input} type="number" value={newPromo.discount_value ?? ''} onChange={(e) => setNewPromo({ ...newPromo, discount_value: Number(e.target.value) })} /></Field>
              </div>
              <div className="mt-3 flex gap-2">
                <button onClick={() => api.post('/admin/marketplace/promo-codes', newPromo).then(() => { notify('Código criado'); setNewPromo(null); load(); }).catch((e) => notify(readError(e), true))} className="rounded-xl bg-emerald-600 px-4 py-1.5 text-sm font-semibold text-white" data-testid="promo-save">Guardar</button>
                <button onClick={() => setNewPromo(null)} className="rounded-xl border px-3 py-1.5 text-sm">Cancelar</button>
              </div>
            </div>
          )}

          <div>
            <p className="mb-2 text-sm font-semibold text-slate-700">Campanhas ({campaigns.length})</p>
            <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm" data-testid="campaigns-table">
              <thead className="text-[11px] uppercase text-slate-500"><tr><th className="py-2">Nome</th><th>Estado</th><th>Efetivo</th><th>Desconto</th><th>Produtos</th><th>Usos</th><th>Prioridade</th><th>Ações</th></tr></thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100" data-testid={`campaign-row-${c.id}`}>
                    <td className="py-2 font-semibold">{c.name}</td>
                    <td className="text-xs">{c.status}</td>
                    <td className="text-xs">{c.effective_status}</td>
                    <td className="text-xs">{c.makes_product_free ? 'gratuito' : `${c.discount_value} ${c.discount_type}`}</td>
                    <td className="text-xs">{(c.product_ids || []).length}</td>
                    <td className="text-xs">{c.current_uses}{c.max_uses_total ? `/${c.max_uses_total}` : ''}</td>
                    <td className="text-xs">{c.priority}</td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {['schedule', 'activate', 'pause', 'end', 'archive'].map((a) => (
                          <button key={a} onClick={() => campaignAction(c.id, a)} disabled={rowBusy === c.id} className="rounded-lg border px-2 py-1 text-[11px] disabled:opacity-50" data-testid={`campaign-${a}-${c.id}`}>{a}</button>
                        ))}
                        {rowBusy === c.id && <span className="text-[11px] text-slate-500">a atualizar…</span>}
                      </div>
                    </td>
                  </tr>
                ))}
                {!campaigns.length && <tr><td colSpan={8} className="py-3 text-center text-sm text-slate-500">Sem campanhas.</td></tr>}
              </tbody>
            </table>
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-semibold text-slate-700">Códigos promocionais ({promos.length})</p>
            <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-sm" data-testid="promos-table">
              <thead className="text-[11px] uppercase text-slate-500"><tr><th className="py-2">Código</th><th>Estado</th><th>Efetivo</th><th>Desconto</th><th>Usos</th><th>Ações</th></tr></thead>
              <tbody>
                {promos.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100" data-testid={`promo-row-${p.code}`}>
                    <td className="py-2 font-semibold">{p.code}</td>
                    <td className="text-xs">{p.status}</td>
                    <td className="text-xs">{p.effective_status}</td>
                    <td className="text-xs">{p.makes_product_free ? 'gratuito' : `${p.discount_value} ${p.discount_type}`}</td>
                    <td className="text-xs">{p.current_uses}{p.max_uses_total ? `/${p.max_uses_total}` : ''}</td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {['activate', 'pause', 'expire', 'archive'].map((a) => (
                          <button key={a} onClick={() => promoAction(p.id, a)} disabled={rowBusy === p.id} className="rounded-lg border px-2 py-1 text-[11px] disabled:opacity-50" data-testid={`promo-${a}-${p.code}`}>{a}</button>
                        ))}
                        {rowBusy === p.id && <span className="text-[11px] text-slate-500">a atualizar…</span>}
                      </div>
                    </td>
                  </tr>
                ))}
                {!promos.length && <tr><td colSpan={6} className="py-3 text-center text-sm text-slate-500">Sem códigos.</td></tr>}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- ENCOMENDAS ---------------- */}
      {tab === 'encomendas' && (
        <div className="mt-4" data-testid="commerce-orders-section">
          <div className="mb-3 flex flex-wrap items-end gap-2">
            <Field label="Nº encomenda"><input className={input} value={orderFilters.order_number} onChange={(e) => setOrderFilters({ ...orderFilters, order_number: e.target.value })} data-testid="orders-filter-number" /></Field>
            <Field label="Email"><input className={input} value={orderFilters.email} onChange={(e) => setOrderFilters({ ...orderFilters, email: e.target.value })} data-testid="orders-filter-email" /></Field>
            <Field label="Estado">
              <select className={input} value={orderFilters.status} onChange={(e) => setOrderFilters({ ...orderFilters, status: e.target.value })} data-testid="orders-filter-status">
                <option value="">Todos</option>
                {['pending_payment', 'processing', 'paid', 'failed', 'expired', 'cancelled', 'refunded'].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Ambiente">
              <select className={input} value={orderFilters.environment} onChange={(e) => setOrderFilters({ ...orderFilters, environment: e.target.value })}>
                <option value="">Todos</option><option value="test">test</option><option value="live">live</option>
              </select>
            </Field>
            <button onClick={loadOrders} className="rounded-xl border border-slate-300 px-3 py-1.5 text-sm" data-testid="orders-apply-filters">Filtrar</button>
          </div>
          <p className="mb-2 text-xs text-slate-500" data-testid="orders-summary">
            {orders.total} encomenda(s) · {Object.entries(orders.counts_by_status || {}).map(([k, v]) => `${k}: ${v}`).join(' · ')} · painel apenas de consulta
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="orders-table">
              <thead className="text-[11px] uppercase text-slate-500"><tr><th className="py-2">Nº</th><th>Data</th><th>Produto</th><th>Anúncio</th><th>Email</th><th>Valor</th><th>Desc.</th><th>Final</th><th>Estado</th><th>Método</th><th>Ambiente</th><th>Session</th><th>Detalhe</th></tr></thead>
              <tbody>
                {(orders.items || []).map((o) => (
                  <tr key={o.id} className="border-t border-slate-100" data-testid={`order-row-${o.order_number}`}>
                    <td className="py-2 font-mono text-xs">{o.order_number}</td>
                    <td className="text-xs">{(o.created_at || '').slice(0, 16)}</td>
                    <td className="text-xs">{o.product_name || o.product_type}</td>
                    <td className="text-xs">{o.listing_id || '—'}</td>
                    <td className="text-xs">{o.buyer_email || '—'}</td>
                    <td className="text-xs">{o.amount} {o.currency}</td>
                    <td className="text-xs">{o.discount_amount || 0}</td>
                    <td className="text-xs font-semibold">{o.final_amount}</td>
                    <td className="text-xs">{o.status}</td>
                    <td className="text-xs">{o.payment_method_type || '—'}</td>
                    <td className="text-xs">{o.environment}</td>
                    <td className="font-mono text-[11px] text-slate-500">{o.stripe_checkout_session_id_masked || '—'}</td>
                    <td>
                      <button onClick={() => showOrder(o)} disabled={rowBusy === o.id} className="rounded-lg border px-2 py-1 text-[11px] disabled:opacity-50" data-testid={`order-detail-${o.order_number}`}>Detalhe</button>
                    </td>
                  </tr>
                ))}
                {!(orders.items || []).length && (
                  <tr><td colSpan={13} className="py-4 text-center text-sm text-slate-500" data-testid="orders-empty">
                    {ordersFiltered ? 'Nenhuma encomenda corresponde aos filtros aplicados.' : 'Sem encomendas (comércio desativado).'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {orderDetail && (
            <div className="mt-4 rounded-xl border border-slate-200 p-3" data-testid="order-detail-panel">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-800">
                  Encomenda {orderDetail.order?.order_number} · {orderDetail.order?.status} · apenas consulta
                </p>
                <button onClick={() => setOrderDetail(null)} className="rounded-lg border px-2 py-1 text-xs" data-testid="order-detail-close">Fechar</button>
              </div>
              <div className="mt-3 grid gap-4 md:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500">Snapshot do produto (imutável)</p>
                  <ul className="mt-1 space-y-0.5 text-xs text-slate-600" data-testid="order-detail-snapshot">
                    {Object.entries(orderDetail.order?.product_snapshot || {}).map(([k, v]) => (
                      <li key={k}>{k}: <span className="font-medium text-slate-800">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span></li>
                    ))}
                    {!Object.keys(orderDetail.order?.product_snapshot || {}).length && <li>—</li>}
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500">Histórico de estados</p>
                  <ul className="mt-1 space-y-0.5 text-xs text-slate-600" data-testid="order-detail-history">
                    {(orderDetail.order?.status_history || []).map((h, i) => (
                      <li key={i}>{(h.at || h.timestamp || '').slice(0, 19)} · {h.from || '—'} → {h.to || h.status} {h.source ? `· ${h.source}` : ''}</li>
                    ))}
                    {!(orderDetail.order?.status_history || []).length && <li>—</li>}
                  </ul>
                  <p className="mt-3 text-xs font-semibold uppercase text-slate-500">Eventos Stripe (mascarados)</p>
                  <ul className="mt-1 space-y-0.5 text-xs text-slate-600" data-testid="order-detail-events">
                    {(orderDetail.stripe_events || []).map((e, i) => (
                      <li key={i}>{(e.received_at || '').slice(0, 19)} · {e.event_type} · {e.processing_status} · {e.event_id}</li>
                    ))}
                    {!(orderDetail.stripe_events || []).length && <li>Sem eventos.</li>}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
